// Landing page from the vendor-invite email. Same mechanism as
// PortalSetPassword: Supabase emits PASSWORD_RECOVERY / SIGNED_IN from
// the URL hash, then we call updateUser to set the password.
//
// Mounted at /vendor/set-password — matches VENDOR_PORTAL_BASE_URL
// configured in the vendor-invite function's env.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Lock, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export default function VendorSetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [tokenAccepted, setTokenAccepted] = useState(false)

  useEffect(() => {
    let resolved = false
    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (!resolved && session?.user && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'USER_UPDATED')) {
        resolved = true
        setTokenAccepted(true)
      }
    })
    supabase.auth.getSession().then(({ data }) => {
      if (!resolved && data.session?.user) { resolved = true; setTokenAccepted(true) }
    })
    return () => { sub.data.subscription.unsubscribe() }
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return }
    if (password !== confirm) { setErr('Passwords don\'t match'); return }
    setBusy(true); setErr(null)
    const { error } = await supabase.auth.updateUser({ password })
    if (error) {
      setBusy(false)
      setErr(error.message || 'Failed to set password')
      return
    }
    setDone(true)
    setTimeout(() => navigate('/vendor', { replace: true }), 800)
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Zap style={{ width: 18, height: 18, color: 'var(--accent-ink)' }} />
          </div>
          <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>
            Vendor Portal
          </p>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24 }}>
          {!tokenAccepted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--ink-secondary)' }}>
              <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
              Validating invite link…
            </div>
          ) : done ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--positive)' }}>
              <CheckCircle2 style={{ width: 16, height: 16 }} />
              Password set. Signing you in…
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Set your password</h1>
              <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: 0 }}>
                Pick a password — at least 8 characters. You'll use it to sign in next time.
              </p>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>New password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus
                    style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confirm</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: 'var(--ink-tertiary)' }} />
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                    style={{ width: '100%', padding: '10px 12px 10px 33px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              {err && (
                <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', borderRadius: 7 }}>
                  <AlertCircle style={{ width: 13, height: 13, color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 12, color: 'var(--danger)' }}>{err}</span>
                </div>
              )}
              <button type="submit" disabled={busy}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 14, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />}
                {busy ? 'Saving…' : 'Save and continue'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
